import { useState } from "react";
import { Trash2 } from "lucide-react";

import { HealthmailLoginCardButton } from "components/HealthmailLoginCard/HealthmailLoginCardButton";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable/DataTable";
import { PageSection } from "components/ui/PageSection";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useDrugFavourites } from "features/medications/hooks/useDrugFavourites";

import styles from "./DrugFavouritesTab.module.css";

const COLUMNS = [
  {
    id: "tradeName",
    header: "Trade Name",
    width: "495px",
    render: (row) => <strong>{row.tradeName || "-"}</strong>,
  },
  {
    id: "genericName",
    header: "Generic Name",
    width: "280px",
    render: (row) => row.genericName || "-",
  },
  {
    id: "manufacturer",
    header: "Manufacturer",
    width: "240px",
    render: (row) => row.manufacturer || "-",
  },
  {
    id: "strength",
    header: "Strength",
    width: "200px",
    render: (row) => row.strength || "-",
  },
  {
    id: "form",
    header: "Form",
    width: "200px",
    render: (row) =>
      row.form ? <Pill {...PILL_COLORS.method}>{row.form}</Pill> : "-",
  },
  {
    id: "packSize",
    header: "Pack Size",
    width: "minmax(200px, 1fr)",
    render: (row) => row.packSize || "-",
  },
];

export const DrugFavouritesTab = () => {
  const {
    drugFavourites,
    drugFavouritesLoading,
    drugSearchQuery,
    setDrugSearchQuery,
    drugSearchResults,
    drugSearchLoading,
    addingFavourite,
    addDrugFavourite,
    removeDrugFavourite,
  } = useDrugFavourites();

  const [drugToRemove, setDrugToRemove] = useState(null);

  const rowActions = [
    {
      label: "Remove from favourites",
      icon: Trash2,
      primary: true,
      onClick: (row) => setDrugToRemove(row),
    },
  ];

  return (
    <PageSection>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Drug Favourites</h3>
        <HealthmailLoginCardButton variant="text" size="medium" />
      </div>

      <div className={styles.searchRow}>
        <div className={styles.searchWrapper}>
          <label className={styles.searchLabel}>Search drugs</label>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search drugs by name (min 2 characters)..."
            value={drugSearchQuery}
            onChange={(e) => setDrugSearchQuery(e.target.value)}
          />
          {drugSearchQuery.length >= 2 && (
            <div className={styles.dropdown}>
              {drugSearchLoading ? (
                <div className={styles.dropdownMessage}>Searching...</div>
              ) : drugSearchResults.length === 0 ? (
                <div className={styles.dropdownMessage}>No drugs found</div>
              ) : (
                drugSearchResults.map((drug) => {
                  const isAlreadyFavourite = drugFavourites.some(
                    (fav) => fav.id === drug.id
                  );
                  return (
                    <div
                      key={drug.id}
                      className={`${styles.drugItem} ${
                        isAlreadyFavourite ? styles.drugItemDisabled : ""
                      }`}
                    >
                      <div className={styles.drugItemInfo}>
                        <div className={styles.drugItemName}>
                          {drug.tradeName || "-"}
                          {drug.isFavourite && (
                            <span className={styles.drugItemFavouriteIcon}>
                              ★
                            </span>
                          )}
                        </div>
                        <div className={styles.drugItemMeta}>
                          {drug.genericName || "-"} | {drug.manufacturer || "-"}{" "}
                          | {drug.strength || "-"} | {drug.form || "-"}
                        </div>
                      </div>
                      {isAlreadyFavourite ? (
                        <Button size="small" disabled>
                          Already added
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          onClick={() => addDrugFavourite(drug)}
                          disabled={addingFavourite}
                        >
                          + Add
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        <p className={styles.description}>
          Search for drugs below to add them to your favourites list. Your
          favourite drugs will appear at the top when prescribing.
        </p>
      </div>

      <DataTable
        columns={COLUMNS}
        data={drugFavourites}
        isLoading={drugFavouritesLoading}
        rowActions={rowActions}
        emptyTitle="No favourite drugs added yet"
        emptyText="Use the search above to find and add drugs to your favourites"
        noScroll
      />

      <ConfirmDialog
        isOpen={!!drugToRemove}
        onClose={() => setDrugToRemove(null)}
        onConfirm={() => {
          removeDrugFavourite(drugToRemove);
          setDrugToRemove(null);
        }}
        title="Remove from favourites"
        message={`Are you sure you want to remove ${drugToRemove?.tradeName || drugToRemove?.genericName || "this drug"} from your favourites?`}
        confirmLabel="Remove"
        confirmVariant="danger"
      />
    </PageSection>
  );
};
